import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField, MatHint, MatLabel, MatOption, MatSelect } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { ProgramPartnerService } from '@k12/admin-services';
import { MatInput } from '@angular/material/input';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageCenterService } from '@k12/messaging';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastMessageService } from '@k12/app-services';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MsalService } from '@azure/msal-angular';
import { 
  MessageCenterCategory, 
  MessageCenterChildCategory, 
  SendAccountMessagePayload, 
  ProgramPartner, 
  ToastMessageType 
} from '@k12/models';

type QuickChatRecipientSource = 'hdma' | 'lastUpdatedBy';

interface QuickChatRecipientOption {
  id: string;
  name: string;
  source: QuickChatRecipientSource;
}

export interface MessageCenterSendDialogData {
  programPartnerId: string;
  programPartnerTypeId: number | null;
  taskName?: string;
  recipientId?: string | null;
  hdmaId: string | null;
  hdmaName?: string | null;
  lastUpdatedByAccountId?: string | null;
  lastUpdatedByName?: string | null;
  allowRecipientSelection?: boolean;
}

const ERROR_SENDING_ACCOUNT_MESSAGE = 'Error sending account message';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-message-center-send-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatOption,
    MatSelect,
    MatFormField,
    MatIconModule,
    MatInput,
    MatLabel,
    TextFieldModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FormsModule,
    MatHint,
    MatProgressBar
  ],
  templateUrl: './message-center-send-dialog.component.html',
  styleUrl: './message-center-send-dialog.component.scss'
})
export class MessageCenterSendDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<MessageCenterSendDialogComponent>);
  readonly data = inject<MessageCenterSendDialogData>(MAT_DIALOG_DATA);
  readonly programPartnerService = inject(ProgramPartnerService);
  readonly messageService = inject(MessageCenterService);
  readonly toastService = inject(ToastMessageService);
  readonly msalService = inject(MsalService);

  programPartner: ProgramPartner = {} as ProgramPartner;
  categories: MessageCenterCategory[] = [];
  subCategories: MessageCenterChildCategory[] = [];

  selectedCategory: MessageCenterCategory | null = null;
  selectedSubcategory: MessageCenterChildCategory | null = null;
  messageContent: string = '';
  recipientOptions: QuickChatRecipientOption[] = [];
  selectedRecipientId: string | null = null;
  sendingMessage: boolean = false;
  accountId: string = '';

  constructor() {
    this.accountId = this.msalService.instance.getActiveAccount()?.localAccountId ?? '';
    const defaultRecipientId = this.isRecipientSelectionEnabled
      ? this.data.recipientId ?? this.data.hdmaId
      : this.data.hdmaId ?? this.data.recipientId;
    this.selectedRecipientId = this.normalizeValue(defaultRecipientId);
  }

  ngOnInit(): void {
    this.initializeRecipientOptions();
    this.getProgramPartner();
    this.getTopics();
  }

  categoryChange() {
    this.subCategories = this.selectedCategory?.childCategories ?? [];
    this.selectedSubcategory =
      this.subCategories.find((subCategory) => subCategory.id === this.selectedSubcategory?.id) ?? null;
  }

  getTopics() {
    this.messageService.GetMessageTopicsForProgramPartnerType(this.data.programPartnerTypeId).subscribe((res) => {
      this.categories = res;
      this.categoryChange();
    });
  }

  getProgramPartner() {
    this.programPartnerService.getProgramPartner(this.data.programPartnerId).subscribe((res) => {
      this.programPartner = res?.body ?? res ?? ({} as ProgramPartner);
      this.initializeRecipientOptions();
    });
  }

  onKeyUp(event: KeyboardEvent) {
    if ((event.key === 'Enter' || event.key === 'Return') && this.canSendMessage) {
      this.btnSendMessage();
    }
  }

  btnSendMessage() {
    const recipientId = this.normalizeValue(this.selectedRecipientId) ?? this.resolveHdmaRecipientId();
    if (!recipientId) {
      this.toastService.showToast(ToastMessageType.Error, ERROR_SENDING_ACCOUNT_MESSAGE);
      this.sendingMessage = false;
      return;
    }

    this.sendingMessage = true;
    const selectedSubcategory =
      this.subCategories.find((subCategory) => subCategory.id === this.selectedSubcategory?.id) ?? null;
    const childCategoryId = selectedSubcategory?.id;
    const description = selectedSubcategory?.description ?? this.selectedCategory?.description ?? '';

    const topicPayload = {
      programPartnerId: this.data.programPartnerId,
      programPartnerTypeId: this.data.programPartnerTypeId ?? this.programPartner?.programPartnerTypeId ?? null,
      parentCategoryId: this.selectedCategory?.id,
      parentCategoryName: this.selectedCategory?.name,
      childCategoryName: selectedSubcategory?.name ?? '',
      description,
      Description: description,
      participantId: recipientId,
      assignedToId: this.accountId,
      ...(childCategoryId ? { childCategoryId } : {})
    };

    this.messageService.CreateAdminAccountMessageTopic(topicPayload).subscribe({
      next: (topic) => {
        const accountMessageTopicId = this.resolveCreatedTopicId(topic);
        if (!accountMessageTopicId) {
          this.toastService.showToast(ToastMessageType.Error, 'Error creating account message topic');
          this.sendingMessage = false;
          return;
        }

        const sendMessagePayload: SendAccountMessagePayload = {
          message: this.messageContent,
          accountMessageTopicId,
          recipientId: recipientId,
          senderId: this.accountId
        };
        this.messageService.SendAdminAccountMessage(sendMessagePayload).subscribe({
          next: (result) => {
            if (this.isFailedApiResult(result)) {
              this.toastService.showToast(ToastMessageType.Error, ERROR_SENDING_ACCOUNT_MESSAGE);
              this.sendingMessage = false;
              return;
            }
            this.messageContent = '';
            this.dialogRef.close(true);
          },
          error: () => {
            this.toastService.showToast(ToastMessageType.Error, ERROR_SENDING_ACCOUNT_MESSAGE);
            this.sendingMessage = false;
          }
        });
      },
      error: () => {
        this.toastService.showToast(ToastMessageType.Error, 'Error creating account message topic');
        this.sendingMessage = false;
      }
    });
  }

  get isCategoryValid(): boolean {
    return this.selectedCategory != null && (this.subCategories.length !== 0 ? this.selectedSubcategory != null : true);
  }

  get canSendMessage(): boolean {
    const messageContentIsValid = this.messageContent.length > 0 && this.messageContent.length <= 500;
    return this.isCategoryValid && messageContentIsValid && !!this.selectedRecipientId;
  }

  get showRecipientSelection(): boolean {
    return this.isRecipientSelectionEnabled;
  }

  getRecipientLabel(recipientOption: QuickChatRecipientOption): string {
    const recipientRole = recipientOption.source === 'hdma' ? 'HDMA' : 'Last Updated By';
    return `${recipientOption.name} - ${recipientRole}`;
  }

  private initializeRecipientOptions(): void {
    const hdmaRecipientId = this.resolveHdmaRecipientId();
    const lastUpdatedByAccountId = this.normalizeValue(this.data.lastUpdatedByAccountId);
    const previousSelectedRecipientId = this.selectedRecipientId;
    const options: QuickChatRecipientOption[] = [];

    if (hdmaRecipientId) {
      options.push({
        id: hdmaRecipientId,
        name: this.resolveHdmaRecipientName(hdmaRecipientId),
        source: 'hdma'
      });
    }

    if (this.isRecipientSelectionEnabled && lastUpdatedByAccountId && lastUpdatedByAccountId !== hdmaRecipientId) {
      options.push({
        id: lastUpdatedByAccountId,
        name: this.resolveLastUpdatedByName(lastUpdatedByAccountId),
        source: 'lastUpdatedBy'
      });
    }

    this.recipientOptions = options;
    if (!this.isRecipientSelectionEnabled) {
      this.selectedRecipientId = hdmaRecipientId ?? options[0]?.id ?? null;
      return;
    }

    this.selectedRecipientId =
      previousSelectedRecipientId && options.some((option) => option.id === previousSelectedRecipientId)
        ? previousSelectedRecipientId
        : options[0]?.id ?? null;
  }

  private get isRecipientSelectionEnabled(): boolean {
    if (this.data.allowRecipientSelection !== undefined) {
      return this.data.allowRecipientSelection;
    }

    return !!this.normalizeValue(this.data.taskName);
  }

  private resolveHdmaRecipientId(): string | null {
    return this.normalizeValue(this.data.hdmaId ?? this.data.recipientId ?? this.programPartner?.accountId);
  }

  private resolveCreatedTopicId(topic: unknown): string | null {
    if (!topic || typeof topic !== 'object' || this.isFailedApiResult(topic)) {
      return null;
    }

    const record = topic as Record<string, unknown>;
    const entity = record['entity'];
    const entityRecord = entity && typeof entity === 'object' ? (entity as Record<string, unknown>) : undefined;
    const id =
      record['id'] ??
      entityRecord?.['id'] ??
      record['accountMessageTopicId'] ??
      entityRecord?.['accountMessageTopicId'];

    return id != null ? this.normalizeValue(String(id)) : null;
  }

  private isFailedApiResult(result: unknown): boolean {
    return !!result && typeof result === 'object' && (result as { success?: unknown }).success === false;
  }

  private normalizeValue(value: string | null | undefined): string | null {
    const trimmedValue = value?.trim();
    return trimmedValue ? trimmedValue : null;
  }

  private resolveHdmaRecipientName(hdmaRecipientId: string): string {
    return (
      this.normalizeValue(this.data.hdmaName) ??
      this.normalizeValue(this.programPartner?.hdmaName) ??
      this.normalizeValue(this.programPartner?.displayName) ??
      hdmaRecipientId
    );
  }

  private resolveLastUpdatedByName(lastUpdatedByAccountId: string): string {
    return this.normalizeValue(this.data.lastUpdatedByName) ?? lastUpdatedByAccountId;
  }
}
